// ── RRN Utilities ─────────────────────────────────────────────
/**
 * Validates a Belgian RRN (rijksregisternummer).
 * Accepts raw digits or formatted input (e.g. "85.07.30-033-28" or "85073003328").
 * Uses the modulo-97 checksum algorithm.
 */
export function validateRRN(raw) {
    const digits = raw.replace(/\D/g, '');
    if (digits.length !== 11)
        return false;
    const base9 = digits.slice(0, 9);
    const cc = parseInt(digits.slice(9), 10);
    // Born in the 1900s
    if (97 - (parseInt(base9, 10) % 97) === cc)
        return true;
    // Born in the 2000s (prefix base with '2')
    if (97 - (parseInt('2' + base9, 10) % 97) === cc)
        return true;
    return false;
}
/**
 * Derives the ISO birth date (YYYY-MM-DD) from a Belgian RRN.
 * Uses the same checksum logic as validateRRN to determine the century.
 * Returns null for invalid or unrecognised RRNs.
 */
export function birthDateFromRRN(raw) {
    const digits = raw.replace(/\D/g, '');
    if (digits.length !== 11)
        return null;
    const yy = digits.slice(0, 2);
    const dd = digits.slice(4, 6);
    const base9 = digits.slice(0, 9);
    const cc = parseInt(digits.slice(9), 10);
    let century;
    if (97 - (parseInt(base9, 10) % 97) === cc) {
        century = 1900;
    }
    else if (97 - (parseInt('2' + base9, 10) % 97) === cc) {
        century = 2000;
    }
    else {
        return null;
    }
    // BIS-numbers (foreign nationals) offset the month by +20 (sex unknown) or +40 (sex known).
    const rawMm = parseInt(digits.slice(2, 4), 10);
    const mm = (rawMm > 40 ? rawMm - 40 : rawMm > 20 ? rawMm - 20 : rawMm).toString().padStart(2, '0');
    return `${century + parseInt(yy, 10)}-${mm}-${dd}`;
}
export function sexFromRRN(raw) {
    const digits = raw.replace(/\D/g, '');
    if (digits.length !== 11)
        return null;
    return parseInt(digits.slice(6, 9), 10) % 2 === 1 ? 'male' : 'female';
}
/** Current version of the usage agreement / consent text shown to users. */
export const TOS_VERSION = '1.0';
/** Intake informed-consent versions (legal intake flow). */
export const INTAKE_INFORMED_CONSENT_VERSION = '1.3';
export const MEDICAL_DISCLAIMER_VERSION = '1.0';
export const USER_AGREEMENT_VERSION = '1.0';
export const PRIVACY_POLICY_VERSION = '1.1';
/** Consultation tariffs, in cents. */
export const PRESCRIPTION_CONSULT_PRICE_CENTS = 500;
export const NEW_CONSULT_PRICE_CENTS = 3200;
/**
 * Dermalink's application fee per paid consult on a practice's own Mollie account (Mollie
 * Connect), in cents incl. 21% Belgian VAT. The consult itself is VAT-exempt (medical care,
 * art. 44 W.BTW); the fee is a separate taxable platform service invoiced to the practice.
 */
export const PLATFORM_FEE_CENTS = { new_consult: 770, prescription: 170 };
export const PLATFORM_FEE_VAT_RATE = 0.21;
/** Splits a VAT-inclusive amount; VAT is taken on the total so per-line rounding can't drift. */
export function splitVatInclusive(totalCents) {
    const net = Math.round(totalCents / (1 + PLATFORM_FEE_VAT_RATE));
    return { net_cents: net, vat_cents: totalCents - net };
}
// ── Operator: Recoverable (Soft-Deleted) Conversations ────────
/** closed_reason values eligible for reopening to 'active' on restore — payment-related and
 *  patient-confirmed closures represent a completed transaction/decision and are never eligible.
 *  Necessary but not sufficient: a conversation with a paid payment is never reopenable either,
 *  regardless of closed_reason — see RecoverableConversationItem.has_paid_payment. */
export const REOPENABLE_CLOSED_REASONS = ['auto_closed_inactivity', 'force_closed'];
// ── Operator: Organizations ───────────────────────────────────
export const ORGANIZATION_TYPES = ['clinic', 'hospital'];
/** URL-safe slug for patient deeplinks (/praktijk/:slug). Mirrors the backfill in migration 043. */
export function slugifyOrganizationName(name) {
    return name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
/** Height logos are rendered at by the cropper (≈4× the header's 32px, for high-DPI screens). */
export const LOGO_HEIGHT = 128;
/** width / height bounds: a square symbol up to a wide wordmark. */
export const LOGO_MIN_ASPECT = 1;
export const LOGO_MAX_ASPECT = 4;
/** Lowercase '#rrggbb' — the only brand colour format stored and served. */
export const BRAND_COLOR_PATTERN = /^#[0-9a-f]{6}$/;
/** The billing kinds a doctor picks when closing a consult, in display order. */
export const CONSULT_REPORT_TYPES = ['new_consult', 'prescription', 'follow_up'];
const RICH_TEXT_INLINE_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|(https?:\/\/[^\s<>"]+[^\s<>".,;:!?)])/g;
function parseRichTextInline(text) {
    const nodes = [];
    let lastIndex = 0;
    for (const match of text.matchAll(RICH_TEXT_INLINE_REGEX)) {
        const index = match.index ?? 0;
        if (index > lastIndex)
            nodes.push({ type: 'text', value: text.slice(lastIndex, index) });
        const [, linkText, linkUrl, bold, bareUrl] = match;
        if (linkText !== undefined) {
            nodes.push({ type: 'link', text: linkText, url: linkUrl });
        }
        else if (bold !== undefined) {
            nodes.push({ type: 'bold', value: bold });
        }
        else {
            nodes.push({ type: 'link', text: bareUrl, url: bareUrl });
        }
        lastIndex = index + match[0].length;
    }
    if (lastIndex < text.length)
        nodes.push({ type: 'text', value: text.slice(lastIndex) });
    return nodes;
}
export function parseRichText(text) {
    const lines = text.split('\n');
    const blocks = [];
    let paragraphLines = [];
    let listItems = [];
    function flushParagraph() {
        if (!paragraphLines.length)
            return;
        blocks.push({ type: 'paragraph', inline: parseRichTextInline(paragraphLines.join('\n')) });
        paragraphLines = [];
    }
    function flushList() {
        if (!listItems.length)
            return;
        blocks.push({ type: 'list', items: listItems.map(parseRichTextInline) });
        listItems = [];
    }
    for (const line of lines) {
        const bulletMatch = /^-\s+(.*)$/.exec(line);
        if (bulletMatch) {
            flushParagraph();
            listItems.push(bulletMatch[1]);
        }
        else {
            flushList();
            paragraphLines.push(line);
        }
    }
    flushParagraph();
    flushList();
    return blocks;
}
// ── Image Uploads ─────────────────────────────────────────────
/** MIME types accepted for message attachments (enforced on both client and API). */
export const ALLOWED_IMAGE_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
];
//# sourceMappingURL=index.js.map